import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, BellRing, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { spaceOf, SUPPORT_EMAIL } from "@/lib/spaces";
import { fromNow } from "@/lib/time";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSettings } from "@/context/SettingsContext";
import { playSound } from "@/lib/fun";

const canNotify = () => typeof window !== "undefined" && "Notification" in window;

export const NotificationBell = () => {
  const { refresh } = useApp();
  const { prefs } = useSettings();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const [data, setData] = useState({ items: [], unread: 0 });
  const [perm, setPerm] = useState(canNotify() ? Notification.permission : "unsupported");
  const known = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      if (known.current) {
        const fresh = data.items.filter((n) => !known.current.has(n.id));
        fresh.forEach((n) => {
          const who = spaceOf(n.space).name;
          toast(`Reminder: ${n.title}`, { description: `${who}'s space · emailed ${who} + support` });
          if (prefsRef.current.browser_alerts !== false && canNotify() && Notification.permission === "granted") new Notification(`Atom IP: ${n.title}`, { body: `Reminder for ${who}` });
        });
        if (fresh.length && prefsRef.current.sounds) playSound(prefsRef.current.sound_pack, "alert");
        if (fresh.length) refresh();
      }
      known.current = new Set(data.items.map((n) => n.id));
      setData(data);
    } catch {
      /* polling is best-effort */
    }
  }, [refresh]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [poll]);

  const enable = async () => setPerm(await Notification.requestPermission());
  const readAll = async () => {
    await api.post("/notifications/read-all");
    poll();
  };
  const digest = async () => {
    try {
      await api.post("/digest/send-now");
      toast.success(`Digest on its way to ${SUPPORT_EMAIL}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative flex h-10 w-10 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">
          {data.unread ? <BellRing className="h-4 w-4 text-amber-400" /> : <Bell className="h-4 w-4" />}
          {data.unread > 0 && <span data-testid="notifications-unread-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 font-mono text-[10px] font-bold text-black">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="font-display text-sm font-semibold">Fired reminders</p>
          <button onClick={readAll} className="text-xs text-muted-foreground hover:text-foreground" data-testid="notifications-read-all">Mark all read</button>
        </div>
        <div className="scrollbar-thin max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nothing has fired yet.</p>}
          {data.items.map((n) => (
            <div key={n.id} className={`flex gap-3 border-b px-4 py-3 last:border-0 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
              <SpaceAvatar space={n.space} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground">{spaceOf(n.space).name} · {fromNow(n.created_at)}</p>
              </div>
              {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-amber-400" />}
            </div>
          ))}
        </div>
        <div className="space-y-1 border-t p-2">
          {perm !== "granted" && perm !== "unsupported" && (
            <button onClick={enable} data-testid="enable-desktop-alerts" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-white/5">
              <BellRing className="h-3.5 w-3.5" /> Enable desktop alerts
            </button>
          )}
          <button onClick={digest} data-testid="send-digest-now" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-white/5">
            <Mail className="h-3.5 w-3.5" /> Email digest to support now
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
